// ============================================
// Master Formulator — Seed Interactions
// Tier 1: Established Drug-Supplement Interactions
// Source: NatMed Professional Database, Lexicomp,
// FDA MedWatch, published clinical trials
// ============================================

import type { DrugInteraction } from '../../types/masterFormulator';

export const SEED_INTERACTIONS: Partial<DrugInteraction>[] = [
  // ─────────────────────────────────────────────
  // 1. Fish Oil (Omega-3) + Warfarin
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-OMEGA3-WARFARIN',
    substanceA: 'Omega-3 Fatty Acids (EPA/DHA)',
    substanceAType: 'supplement',
    substanceAId: 'ING-EPA',
    substanceB: 'Warfarin (Coumadin)',
    substanceBType: 'pharmaceutical',
    severity: 'major',
    mechanism: 'EPA/DHA inhibit platelet aggregation via COX-1 pathway and reduce thromboxane A2 synthesis, potentiating warfarin anticoagulant effect. At doses >2g/day, may independently prolong bleeding time.',
    clinicalEffect: 'Increased bleeding risk including GI hemorrhage, easy bruising, epistaxis, and potentially life-threatening hemorrhagic events.',
    management: 'BLOCK: Do not recommend omega-3 supplements to patients on warfarin without explicit provider authorization. If provider approves, require weekly INR monitoring for first month, then biweekly. Maximum dose 1g/day EPA+DHA with warfarin.',
    actionRequired: 'block',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Buckley MS, Goff AD, Knapp WE',
        title: 'Fish oil interaction with warfarin',
        journal: 'Ann Pharmacother',
        year: 2004,
        volume: '38',
        pages: '50-52',
        doi: '10.1345/aph.1D007',
      },
      {
        authors: 'Bays HE',
        title: 'Safety considerations with omega-3 fatty acid therapy',
        journal: 'Am J Cardiol',
        year: 2007,
        volume: '99',
        pages: '35C-43C',
        doi: '10.1016/j.amjcard.2006.11.020',
      },
    ],
    source: 'NatMed Professional Database; Lexicomp',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 2. Vitamin D3 + Thiazide Diuretics
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-VITD3-THIAZIDE',
    substanceA: 'Vitamin D3 (Cholecalciferol)',
    substanceAType: 'supplement',
    substanceAId: 'ING-VITD3',
    substanceB: 'Thiazide Diuretics (HCTZ, chlorthalidone)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'Thiazide diuretics reduce renal calcium excretion. Combined with vitamin D (which increases intestinal calcium absorption), this can lead to hypercalcemia through dual mechanisms of increased absorption and decreased excretion.',
    clinicalEffect: 'Risk of hypercalcemia. Symptoms include fatigue, nausea, constipation, polyuria, confusion. Severe cases may cause cardiac arrhythmias, renal calculi, or nephrocalcinosis.',
    management: 'WARN PROVIDER: May continue vitamin D with monitoring. Check serum calcium at baseline and 4-8 weeks after initiation. Maintain adequate hydration. If calcium >10.5 mg/dL, reduce vitamin D dose or discontinue.',
    actionRequired: 'warn_provider',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Parfitt AM',
        title: 'Thiazide-induced hypercalcemia in vitamin D-treated hypoparathyroidism',
        journal: 'Ann Intern Med',
        year: 1972,
        volume: '77',
        pages: '557-563',
        doi: '10.7326/0003-4819-77-4-557',
      },
    ],
    source: 'NatMed Professional Database',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 3. Vitamin K + Warfarin
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-VITK-WARFARIN',
    substanceA: 'Vitamin K (phylloquinone/menaquinone)',
    substanceAType: 'supplement',
    substanceB: 'Warfarin (Coumadin)',
    substanceBType: 'pharmaceutical',
    severity: 'critical',
    mechanism: 'Warfarin inhibits vitamin K epoxide reductase (VKORC1) to prevent clotting factor synthesis. Exogenous vitamin K directly antagonizes warfarin by providing substrate for clotting factor production, bypassing the warfarin-inhibited pathway.',
    clinicalEffect: 'Direct antagonism of warfarin anticoagulation. Even small supplemental doses of vitamin K can cause significant INR reduction, leading to therapeutic failure and increased thromboembolic risk (DVT, PE, stroke).',
    management: 'BLOCK: Absolutely contraindicated. Do NOT recommend any vitamin K supplement (K1 or K2) to warfarin patients. If multivitamin contains vitamin K, switch to K-free formulation. Patients should also maintain consistent dietary vitamin K intake.',
    actionRequired: 'block',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Booth SL, Centurelli MA',
        title: 'Vitamin K: a practical guide to the dietary management of patients on warfarin',
        journal: 'Nutr Rev',
        year: 1999,
        volume: '57',
        pages: '288-296',
        doi: '10.1111/j.1753-4887.1999.tb01816.x',
      },
    ],
    source: 'NatMed Professional Database; FDA MedWatch',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 4. DHEA + Insulin
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-DHEA-INSULIN',
    substanceA: 'DHEA (Dehydroepiandrosterone)',
    substanceAType: 'supplement',
    substanceAId: 'ING-DHEA',
    substanceB: 'Insulin (all forms)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'DHEA may improve insulin sensitivity through AMPK activation and PPAR-gamma modulation. In insulin-dependent patients, this additive glucose-lowering effect may cause unpredictable blood glucose fluctuations, particularly hypoglycemia.',
    clinicalEffect: 'Potential for hypoglycemia in insulin-dependent patients. May require insulin dose adjustment. Effect magnitude varies by DHEA dose and patient metabolic status.',
    management: 'WARN PROVIDER: If provider approves DHEA for insulin-dependent patient, increase glucose monitoring frequency (4x/day minimum for first 2 weeks). May require 10-20% insulin dose reduction. Document baseline HbA1c.',
    actionRequired: 'warn_provider',
    evidenceLevel: 'probable',
    citations: [
      {
        authors: 'Dhatariya K, Bigelow ML, Nair KS',
        title: 'Effect of dehydroepiandrosterone replacement on insulin sensitivity and lipids in hypoadrenal women',
        journal: 'Diabetes',
        year: 2005,
        volume: '54',
        pages: '765-769',
        doi: '10.2337/diabetes.54.3.765',
      },
    ],
    source: 'NatMed Professional Database',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 5. DHEA + Hormone Therapy
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-DHEA-HORMONE',
    substanceA: 'DHEA (Dehydroepiandrosterone)',
    substanceAType: 'supplement',
    substanceAId: 'ING-DHEA',
    substanceB: 'Hormone Replacement Therapy (estrogen, testosterone, progesterone)',
    substanceBType: 'pharmaceutical',
    severity: 'major',
    mechanism: 'DHEA is a precursor hormone that converts to both androgens (testosterone, DHT) and estrogens (estradiol, estrone) via peripheral aromatase and 5-alpha-reductase. Co-administration with exogenous hormones creates unpredictable additive/supraphysiologic hormone levels.',
    clinicalEffect: 'Additive estrogenic or androgenic effects. May cause supraphysiologic hormone levels leading to adverse effects: estrogen excess (breast tenderness, bloating, thrombotic risk), androgen excess (acne, hirsutism, male pattern hair loss).',
    management: 'WARN PROVIDER: Monitor total and free testosterone, estradiol, and DHEA-S at baseline and 6-8 weeks. Provider should adjust HRT dosing to account for DHEA contribution. Consider whether DHEA replaces the need for HRT or vice versa.',
    actionRequired: 'warn_provider',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Arlt W, Callies F, van Vlijmen JC, et al.',
        title: 'Dehydroepiandrosterone replacement in women with adrenal insufficiency',
        journal: 'N Engl J Med',
        year: 1999,
        volume: '341',
        pages: '1013-1020',
        doi: '10.1056/NEJM199909303411401',
      },
    ],
    source: 'NatMed Professional Database; Endocrine Society Guidelines',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 6. Ashwagandha + Thyroid Medications
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-ASHWAGANDHA-THYROID',
    substanceA: 'Ashwagandha (Withania somnifera)',
    substanceAType: 'herb',
    substanceAId: 'ING-ASHWAGANDHA',
    substanceB: 'Thyroid Medications (levothyroxine, liothyronine)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'Ashwagandha has demonstrated thyroid-stimulating activity in clinical trials, increasing serum T3 and T4 levels. When combined with exogenous thyroid hormone, may produce additive thyroid hormone elevation leading to iatrogenic thyrotoxicosis.',
    clinicalEffect: 'Potential for thyroid hormone excess: tachycardia, anxiety, tremor, heat intolerance, weight loss, insomnia. May also alter TSH levels making dose titration unreliable.',
    management: 'WARN PROVIDER: Check TSH and free T4 at baseline and 8 weeks after starting ashwagandha. Provider may need to reduce thyroid medication dose. Watch for signs of thyrotoxicosis. Particularly important in Hashimoto patients with variable thyroid function.',
    actionRequired: 'warn_provider',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Sharma AK, Basu I, Singh S',
        title: 'Efficacy and safety of ashwagandha root extract in subclinical hypothyroid patients: a double-blind, randomized placebo-controlled trial',
        journal: 'J Altern Complement Med',
        year: 2018,
        volume: '24',
        pages: '243-248',
        doi: '10.1089/acm.2017.0183',
      },
    ],
    source: 'NatMed Professional Database',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 7. Ashwagandha + Sedatives
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-ASHWAGANDHA-SEDATIVE',
    substanceA: 'Ashwagandha (Withania somnifera)',
    substanceAType: 'herb',
    substanceAId: 'ING-ASHWAGANDHA',
    substanceB: 'Sedatives (benzodiazepines, barbiturates, zolpidem)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'Ashwagandha exhibits GABAergic activity through withanolide A binding to GABA-A receptors. This creates additive CNS depression when combined with sedative medications that also enhance GABAergic transmission.',
    clinicalEffect: 'Additive sedation and CNS depression. May cause excessive drowsiness, impaired coordination, respiratory depression (rare), and prolonged sedation.',
    management: 'WARN PATIENT: Inform patients about additive sedation risk. Advise against driving or operating machinery until effects are known. Start ashwagandha at lowest dose. If daytime drowsiness occurs, reduce ashwagandha dose or take only at bedtime.',
    actionRequired: 'warn_patient',
    evidenceLevel: 'probable',
    citations: [
      {
        authors: 'Candelario M, Cuellar E, Reyes-Ruiz JM, et al.',
        title: 'Direct evidence for GABAergic activity of Withania somnifera on mammalian ionotropic GABAA and GABA rho receptors',
        journal: 'J Ethnopharmacol',
        year: 2015,
        volume: '171',
        pages: '264-272',
        doi: '10.1016/j.jep.2015.05.058',
      },
    ],
    source: 'NatMed Professional Database',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 8. Berberine + Metformin
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-BERBERINE-METFORMIN',
    substanceA: 'Berberine',
    substanceAType: 'supplement',
    substanceAId: 'ING-BERBERINE',
    substanceB: 'Metformin (Glucophage)',
    substanceBType: 'pharmaceutical',
    severity: 'major',
    mechanism: 'Both berberine and metformin activate AMPK and reduce hepatic glucose output. Berberine also improves insulin sensitivity through PPAR-alpha modulation. The combination produces additive glucose-lowering effects that are clinically significant.',
    clinicalEffect: 'Significantly increased risk of hypoglycemia. Additive GI side effects (diarrhea, nausea, abdominal pain). Berberine may also affect metformin pharmacokinetics through OCT transporter modulation.',
    management: 'WARN PROVIDER: The combination can be clinically useful but requires close monitoring. Check fasting glucose and HbA1c at baseline. Increase glucose monitoring to 2-4x/day for first 4 weeks. Provider may need to reduce metformin dose by 25-50%. Monitor for GI intolerance.',
    actionRequired: 'warn_provider',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Yin J, Xing H, Ye J',
        title: 'Efficacy of berberine in patients with type 2 diabetes mellitus',
        journal: 'Metabolism',
        year: 2008,
        volume: '57',
        pages: '712-717',
        doi: '10.1016/j.metabol.2008.01.013',
      },
      {
        authors: 'Zhang Y, Li X, Zou D, et al.',
        title: 'Treatment of type 2 diabetes and dyslipidemia with the natural plant alkaloid berberine',
        journal: 'J Clin Endocrinol Metab',
        year: 2008,
        volume: '93',
        pages: '2559-2565',
        doi: '10.1210/jc.2007-2404',
      },
    ],
    source: 'NatMed Professional Database; Lexicomp',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 9. Berberine + CYP3A4 Substrates
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-BERBERINE-CYP3A4',
    substanceA: 'Berberine',
    substanceAType: 'supplement',
    substanceAId: 'ING-BERBERINE',
    substanceB: 'CYP3A4 Substrates (statins, cyclosporine, macrolides, calcium channel blockers, HIV protease inhibitors)',
    substanceBType: 'pharmaceutical',
    severity: 'major',
    mechanism: 'Berberine is a potent inhibitor of CYP3A4 and CYP2D6 enzymes. Inhibition of CYP3A4 increases plasma concentrations of CYP3A4-metabolized drugs, potentially to toxic levels. Also inhibits P-glycoprotein efflux pump.',
    clinicalEffect: 'Increased blood levels of CYP3A4 substrates. With statins: increased risk of myopathy/rhabdomyolysis. With cyclosporine: nephrotoxicity. With calcium channel blockers: hypotension. Drug-specific toxicity at elevated concentrations.',
    management: 'WARN PROVIDER: Cross-reference all patient medications against CYP3A4 substrate list. High-risk combinations (cyclosporine, tacrolimus) should be blocked. For lower-risk combinations (statins), provider may approve with drug level monitoring. Separate dosing by 4+ hours.',
    actionRequired: 'warn_provider',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Guo Y, Chen Y, Tan ZR, et al.',
        title: 'Repeated administration of berberine inhibits cytochromes P450 in humans',
        journal: 'Eur J Clin Pharmacol',
        year: 2012,
        volume: '68',
        pages: '213-217',
        doi: '10.1007/s00228-011-1108-2',
      },
    ],
    source: 'NatMed Professional Database; Lexicomp',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 10. Magnesium + Fluoroquinolone Antibiotics
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-MAGNESIUM-ANTIBIOTICS',
    substanceA: 'Magnesium (all forms)',
    substanceAType: 'supplement',
    substanceAId: 'ING-MAGNESIUM-GLYCINATE',
    substanceB: 'Fluoroquinolone Antibiotics (ciprofloxacin, levofloxacin, moxifloxacin)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'Divalent cations (Mg2+, Ca2+, Fe2+, Zn2+) chelate fluoroquinolone antibiotics in the GI tract, forming insoluble complexes that dramatically reduce antibiotic absorption (by 25-90% depending on the antibiotic and cation).',
    clinicalEffect: 'Significantly reduced antibiotic efficacy. Treatment failure for the underlying infection. Potential for development of antibiotic-resistant organisms due to subtherapeutic drug levels.',
    management: 'MONITOR: Do not discontinue magnesium during short-course antibiotics unless directed. Separate magnesium from fluoroquinolone by at least 2 hours before or 6 hours after the antibiotic dose. Inform patient about timing requirements.',
    actionRequired: 'monitor',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Polk RE',
        title: 'Drug-drug interactions with fluoroquinolones',
        journal: 'Am J Med',
        year: 1989,
        volume: '87',
        pages: '76S-81S',
        doi: '10.1016/0002-9343(89)90029-2',
      },
    ],
    source: 'Lexicomp; NatMed Professional Database',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 11. Magnesium + Bisphosphonates
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-MAGNESIUM-BISPHOSPHONATE',
    substanceA: 'Magnesium (all forms)',
    substanceAType: 'supplement',
    substanceAId: 'ING-MAGNESIUM-GLYCINATE',
    substanceB: 'Bisphosphonates (alendronate, risedronate, ibandronate)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'Divalent cations chelate bisphosphonates in the GI tract, forming insoluble complexes. Bisphosphonates already have very low oral bioavailability (0.6-1.5%), so any further reduction from chelation is clinically significant.',
    clinicalEffect: 'Reduced bisphosphonate absorption and therapeutic efficacy. Potential for suboptimal osteoporosis treatment and increased fracture risk.',
    management: 'MONITOR: Separate magnesium from bisphosphonate by at least 2 hours. Bisphosphonates should be taken first thing in the morning on an empty stomach with water only; magnesium can be taken in the evening.',
    actionRequired: 'monitor',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Pazianas M, Abrahamsen B',
        title: 'Osteoporosis treatment: bisphosphonates reign to continue for a few more years, at least?',
        journal: 'Ann NY Acad Sci',
        year: 2016,
        volume: '1376',
        pages: '5-13',
        doi: '10.1111/nyas.13166',
      },
    ],
    source: 'Lexicomp',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 12. Iron + Levothyroxine
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-IRON-THYROID',
    substanceA: 'Iron (all forms)',
    substanceAType: 'supplement',
    substanceAId: 'ING-IRON-BISGLYCINATE',
    substanceB: 'Levothyroxine (Synthroid, Tirosint)',
    substanceBType: 'pharmaceutical',
    severity: 'major',
    mechanism: 'Iron binds levothyroxine in the GI tract, forming insoluble ferric-thyroxine complexes. This reduces levothyroxine absorption by 50-75%, causing clinically significant under-treatment of hypothyroidism.',
    clinicalEffect: 'Reduced thyroid hormone absorption leading to symptomatic hypothyroidism despite adequate dosing: fatigue, weight gain, cold intolerance, constipation, elevated TSH.',
    management: 'WARN PROVIDER: MUST separate iron from levothyroxine by at least 4 hours. Levothyroxine should be taken first thing in the morning on empty stomach; iron should be taken at lunch or evening. Recheck TSH 6-8 weeks after starting iron.',
    actionRequired: 'warn_provider',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Campbell NR, Hasinoff BB, Stalts H, et al.',
        title: 'Ferrous sulfate reduces thyroxine efficacy in patients with hypothyroidism',
        journal: 'Ann Intern Med',
        year: 1992,
        volume: '117',
        pages: '1010-1013',
        doi: '10.7326/0003-4819-117-12-1010',
      },
    ],
    source: 'NatMed Professional Database; Lexicomp',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 13. Iron + Tetracycline Antibiotics
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-IRON-TETRACYCLINE',
    substanceA: 'Iron (all forms)',
    substanceAType: 'supplement',
    substanceAId: 'ING-IRON-BISGLYCINATE',
    substanceB: 'Tetracycline Antibiotics (doxycycline, minocycline, tetracycline)',
    substanceBType: 'pharmaceutical',
    severity: 'major',
    mechanism: 'Iron chelates tetracycline antibiotics in the GI tract forming insoluble iron-tetracycline complexes. This reduces absorption of both the iron and the antibiotic by 50-90%. The interaction is bidirectional.',
    clinicalEffect: 'Treatment failure for the underlying infection due to subtherapeutic antibiotic levels. Also reduces iron absorption, impairing anemia correction.',
    management: 'WARN PROVIDER: Separate iron from tetracycline by at least 2 hours before or 4 hours after the antibiotic. Consider temporarily pausing iron supplementation during short-course antibiotic therapy (7-14 days) if clinically appropriate.',
    actionRequired: 'warn_provider',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Neuvonen PJ',
        title: 'Interactions with the absorption of tetracyclines',
        journal: 'Drugs',
        year: 1976,
        volume: '11',
        pages: '45-54',
        doi: '10.2165/00003495-197611010-00004',
      },
    ],
    source: 'Lexicomp; NatMed Professional Database',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 14. Metformin depletes B12
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-B12-METFORMIN',
    substanceA: 'Vitamin B12 (Cobalamin)',
    substanceAType: 'supplement',
    substanceAId: 'ING-B12',
    substanceB: 'Metformin (Glucophage)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'Metformin interferes with calcium-dependent intrinsic factor-B12 complex absorption in the terminal ileum. Long-term metformin use (>3 years) causes B12 deficiency in 10-30% of patients. This is a drug-induced depletion, not a pharmacokinetic interaction.',
    clinicalEffect: 'Insidious B12 deficiency leading to megaloblastic anemia, peripheral neuropathy (which may be misdiagnosed as diabetic neuropathy), fatigue, and cognitive impairment. Often undiagnosed for years.',
    management: 'MONITOR: All patients on metformin should have B12 levels checked annually. Supplementation with methylcobalamin 1000mcg/day is recommended for metformin users. This is a beneficial pairing — B12 supplementation compensates for metformin-induced depletion.',
    actionRequired: 'monitor',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Aroda VR, Edelstein SL, Goldberg RB, et al.',
        title: 'Long-term metformin use and vitamin B12 deficiency in the Diabetes Prevention Program Outcomes Study',
        journal: 'J Clin Endocrinol Metab',
        year: 2016,
        volume: '101',
        pages: '1754-1761',
        doi: '10.1210/jc.2015-3754',
      },
    ],
    source: 'NatMed Professional Database; ADA Standards of Care',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 15. CoQ10 + Statins
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-COQ10-STATINS',
    substanceA: 'Coenzyme Q10 (CoQ10)',
    substanceAType: 'supplement',
    substanceAId: 'ING-COQ10',
    substanceB: 'Statin Medications (atorvastatin, rosuvastatin, simvastatin, pravastatin)',
    substanceBType: 'pharmaceutical',
    severity: 'minor',
    mechanism: 'Statins inhibit HMG-CoA reductase in the mevalonate pathway, which produces both cholesterol and CoQ10. Long-term statin use reduces endogenous CoQ10 levels by 20-40%. CoQ10 supplementation restores depleted levels. This is a drug-induced depletion interaction.',
    clinicalEffect: 'Statin-induced CoQ10 depletion may contribute to statin myopathy (muscle pain, weakness, fatigue). CoQ10 supplementation may reduce statin-related muscle symptoms. Does NOT interfere with statin cholesterol-lowering efficacy.',
    management: 'INFO: Beneficial pairing. CoQ10 supplementation (100-200mg/day ubiquinol) is recommended for all statin users to mitigate CoQ10 depletion. No dose adjustment of statins needed. Take with statin or separately — no timing restrictions.',
    actionRequired: 'info',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Mortensen SA, Rosenfeldt F, Kumar A, et al.',
        title: 'The effect of coenzyme Q10 on morbidity and mortality in chronic heart failure: results from Q-SYMBIO',
        journal: 'JACC Heart Fail',
        year: 2014,
        volume: '2',
        pages: '641-649',
        doi: '10.1016/j.jchf.2014.06.008',
      },
      {
        authors: 'Qu H, Guo M, Chai H, et al.',
        title: 'Effects of coenzyme Q10 on statin-induced myopathy: an updated meta-analysis of randomized controlled trials',
        journal: 'J Am Heart Assoc',
        year: 2018,
        volume: '7',
        pages: 'e009835',
        doi: '10.1161/JAHA.118.009835',
      },
    ],
    source: 'NatMed Professional Database; ACC/AHA Guidelines',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 16. CoQ10 + Antihypertensive Medications
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-COQ10-ANTIHYPERTENSIVE',
    substanceA: 'Coenzyme Q10 (CoQ10)',
    substanceAType: 'supplement',
    substanceAId: 'ING-COQ10',
    substanceB: 'Antihypertensive Medications (ACE inhibitors, ARBs, calcium channel blockers, beta-blockers)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'CoQ10 has independent blood pressure-lowering effects (systolic -11 mmHg, diastolic -7 mmHg in meta-analysis) through improved endothelial function and reduced peripheral vascular resistance. Combined with antihypertensives, may produce additive hypotension.',
    clinicalEffect: 'Additive blood pressure lowering. May cause symptomatic hypotension: dizziness, lightheadedness, syncope, especially on standing (orthostatic hypotension). More pronounced in elderly patients.',
    management: 'MONITOR: Check blood pressure at baseline, 2 weeks, and 4 weeks after starting CoQ10. If BP drops below patient target, provider may need to reduce antihypertensive dose. Advise patient to rise slowly from sitting/lying position.',
    actionRequired: 'monitor',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Rosenfeldt FL, Haas SJ, Krum H, et al.',
        title: 'Coenzyme Q10 in the treatment of hypertension: a meta-analysis of the clinical trials',
        journal: 'J Hum Hypertens',
        year: 2007,
        volume: '21',
        pages: '297-306',
        doi: '10.1038/sj.jhh.1002138',
      },
    ],
    source: 'NatMed Professional Database',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 17. Zinc + Fluoroquinolone Antibiotics
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-ZINC-ANTIBIOTICS',
    substanceA: 'Zinc (all forms)',
    substanceAType: 'supplement',
    substanceAId: 'ING-ZINC',
    substanceB: 'Fluoroquinolone Antibiotics (ciprofloxacin, levofloxacin, moxifloxacin)',
    substanceBType: 'pharmaceutical',
    severity: 'moderate',
    mechanism: 'Zinc (Zn2+) chelates fluoroquinolone antibiotics in the GI tract, forming insoluble zinc-quinolone complexes. Reduces ciprofloxacin absorption by approximately 50% and levofloxacin by approximately 25%.',
    clinicalEffect: 'Reduced antibiotic efficacy. Risk of treatment failure for underlying infection. Subtherapeutic antibiotic levels may promote antimicrobial resistance.',
    management: 'MONITOR: Separate zinc from fluoroquinolone by at least 2 hours before or 6 hours after the antibiotic. For short-course antibiotics (5-14 days), consider temporarily pausing zinc supplementation.',
    actionRequired: 'monitor',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Lomaestro BM, Bailie GR',
        title: 'Absorption interactions with fluoroquinolones',
        journal: 'Drug Saf',
        year: 1995,
        volume: '12',
        pages: '314-333',
        doi: '10.2165/00002018-199512050-00004',
      },
    ],
    source: 'Lexicomp; NatMed Professional Database',
    retracted: false,
  },

  // ─────────────────────────────────────────────
  // 18. Zinc (High Dose) + Copper Depletion
  // ─────────────────────────────────────────────
  {
    interactionId: 'INT-ZINC-COPPER',
    substanceA: 'Zinc (high dose >40mg/day)',
    substanceAType: 'supplement',
    substanceAId: 'ING-ZINC',
    substanceB: 'Copper (endogenous stores)',
    substanceBType: 'supplement',
    severity: 'moderate',
    mechanism: 'High-dose zinc (>40mg/day) induces intestinal metallothionein synthesis. Metallothionein has higher affinity for copper than zinc, sequestering dietary copper and preventing absorption. Over weeks to months, this depletes body copper stores.',
    clinicalEffect: 'Zinc-induced copper deficiency: microcytic/sideroblastic anemia unresponsive to iron, neutropenia, peripheral neuropathy (myelopathy), osteoporosis. Often misdiagnosed and may be irreversible in severe cases.',
    management: 'MONITOR: For zinc doses >30mg/day, supplement with 1-2mg copper daily. Check serum copper and ceruloplasmin at baseline and every 3 months during high-dose zinc therapy. Monitor CBC for unexplained anemia or neutropenia.',
    actionRequired: 'monitor',
    evidenceLevel: 'established',
    citations: [
      {
        authors: 'Fosmire GJ',
        title: 'Zinc toxicity',
        journal: 'Am J Clin Nutr',
        year: 1990,
        volume: '51',
        pages: '225-227',
        doi: '10.1093/ajcn/51.2.225',
      },
      {
        authors: 'Nations SP, Boyer PJ, Love LA, et al.',
        title: 'Denture cream: an unusual source of excess zinc, leading to hypocupremia and neurologic disease',
        journal: 'Neurology',
        year: 2008,
        volume: '71',
        pages: '639-643',
        doi: '10.1212/01.wnl.0000312375.01068.b2',
      },
    ],
    source: 'NIH Office of Dietary Supplements; NatMed Professional Database',
    retracted: false,
  },
];
